import type { IDatasource, IGetRowsParams } from "ag-grid-community";
import type { VariablesOf } from "gql.tada";
import {
  type FetchPodcastEpisodesFilters,
  GetPodcastEpisodes,
  type PodcastEpisode,
  type PodcastEpisodeOrdering,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import { client } from "@/lib/urqlProvider";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import type { PodcastEpisodeRowData } from "./PodcastEpisodesTableStates";

// Re-export so consumers (e.g. PodcastEpisodesTable) can import the type from here.
export type { PodcastEpisodeOrdering };

const DEFAULT_PAGE_SIZE = 50;

type PodcastEpisodesQueryFilters = VariablesOf<
  typeof GetPodcastEpisodes
>["filters"];

// StrFilterLookup fields must be expanded from plain strings into objects.
// `language` / `sourceCountry` filter lookups expect GraphQL enums; UI values are
// opaque strings — cast at the boundary so the client matches generated types.
const buildGraphQLFilters = (
  filters: FetchPodcastEpisodesFilters,
): PodcastEpisodesQueryFilters => ({
  feedId: filters.feedId,
  publishDateAfter: filters.publishDateAfter,
  publishDateBefore: filters.publishDateBefore,
  sourceTitle: filters.sourceTitle
    ? { iContains: filters.sourceTitle }
    : undefined,
  language: filters.language
    ? (filters.language as PodcastEpisodesQueryFilters["language"])
    : undefined,
  sourceCountry: filters.sourceCountry
    ? (filters.sourceCountry as PodcastEpisodesQueryFilters["sourceCountry"])
    : undefined,
  searchMatch: filters.searchMatch,
  keyMessageIds:
    filters.keyMessageIds && filters.keyMessageIds.length > 0
      ? filters.keyMessageIds
      : undefined,
});

const fetchPodcastEpisodesPage = async (
  filters: FetchPodcastEpisodesFilters | undefined,
  ordering: PodcastEpisodeOrdering | undefined,
  limit: number,
  offset: number,
): Promise<{ episodes: PodcastEpisode[]; totalCount: number }> => {
  const result = await client
    .query(
      GetPodcastEpisodes,
      {
        filters: buildGraphQLFilters(filters ?? {}),
        ordering: ordering ? [ordering] : [],
        pagination: { limit, offset },
      },
      { requestPolicy: "network-only" },
    )
    .toPromise();

  if (result.error) {
    throw new Error(result.error.message);
  }

  const data = result.data?.podcastEpisodes;

  if (!data) {
    return { episodes: [], totalCount: 0 };
  }

  return {
    episodes: (data.results ?? []) as PodcastEpisode[],
    totalCount: data.totalCount ?? 0,
  };
};

const transformEpisodeToRowData = (
  episode: PodcastEpisode,
): PodcastEpisodeRowData => ({
  ID: episode.id,
  TITLE: episode.title ?? null,
  SUBTITLE: episode.subtitle ?? null,
  DESCRIPTION: episode.description ?? null,
  AUTHOR: episode.author ?? null,
  SOURCE_TITLE: episode.sourceTitle ?? null,
  SOURCE_COUNTRY: episode.sourceCountry ?? null,
  LANGUAGE: episode.language ?? null,
  PUBLISH_DATE: episode.publishDate ?? null,
  DURATION: episode.durationFormatted ?? null,
  ARCHIVE_URL: episode.archiveUrl ?? null,
  IMAGE_URL: episode.imageUrl ?? null,
  STATUS: episode.status ?? null,
  ESTIMATED_AUDIENCE: episode.estimatedAudience ?? null,
  TRANSCRIPT_EXCERPT: episode.transcriptExcerpt ?? null,
});

// Module-level session tracking — prevents stale responses from a previous
// datasource instance from mutating store state after filters/ordering change.
let currentSessionId = 0;
let inFlightCountForCurrentSession = 0;
let latestFetchId = 0;

/** Resets session state. Intended for use in tests. */
export const resetPodcastEpisodesDatasourceSession = () => {
  currentSessionId = 0;
  inFlightCountForCurrentSession = 0;
  latestFetchId = 0;
};

/** Call when filters or ordering change so stale in-flight responses are ignored. */
export const startNewPodcastEpisodesDatasourceSession = () => {
  currentSessionId += 1;
  inFlightCountForCurrentSession = 0;
};

/** Creates the AG Grid Infinite Row Model datasource for the podcast episodes table. */
export const createPodcastEpisodesDatasource = (
  getFilters: () => FetchPodcastEpisodesFilters,
  getOrdering: () => PodcastEpisodeOrdering | undefined,
): IDatasource => {
  return {
    getRows: async (params: IGetRowsParams) => {
      const sessionId = currentSessionId;
      const fetchId = ++latestFetchId;
      inFlightCountForCurrentSession += 1;

      const {
        setIsFetchingPodcastEpisodes,
        setPodcastEpisodesError,
        setTotalPodcastEpisodesCount,
      } = useDiscoveryContentManagementStore.getState();

      const filters = getFilters();
      const ordering = getOrdering();

      const startRow = params.startRow ?? 0;
      const endRow = params.endRow ?? 0;
      const limit = endRow > startRow ? endRow - startRow : DEFAULT_PAGE_SIZE;
      const offset = startRow;

      try {
        setIsFetchingPodcastEpisodes(true);
        setPodcastEpisodesError(null);

        const { episodes, totalCount } = await fetchPodcastEpisodesPage(
          filters,
          ordering,
          limit,
          offset,
        );

        const rowData: PodcastEpisodeRowData[] = episodes.map(
          transformEpisodeToRowData,
        );

        // Stale response — still deliver rows to the grid but skip store updates
        // so a newer session's loading state isn't cleared prematurely.
        if (fetchId !== latestFetchId) {
          if (sessionId === currentSessionId) {
            inFlightCountForCurrentSession -= 1;
            if (inFlightCountForCurrentSession === 0) {
              setIsFetchingPodcastEpisodes(false);
            }
          }
          params.successCallback(rowData, totalCount);
          return;
        }

        setTotalPodcastEpisodesCount(totalCount);

        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingPodcastEpisodes(false);
          }
        }

        params.successCallback(rowData, totalCount);
      } catch (error: unknown) {
        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingPodcastEpisodes(false);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            setPodcastEpisodesError(errorMessage);
          }
        }
        params.failCallback();
      }
    },
  };
};
